<template>
    <router-link
        class="card"
        :class="classes"
        :to="{
            name: 'edition',
            params: {
                edition: edition.number,
                language,
                title
            }
        }"
    >
        <div class="cover" ref="fit">
            <visual :src="cover" />
        </div>
        <div class="caption" :style="styles.caption">
            <heading :level="5">{{ surtitle }}</heading>
            <heading :level="2" v-html="edition.title" />
            <paragraph
                :content="edition.excerpt"
                :size="$breakpoint.isS ? 'M' : 'S'"
            />
            <cta
                v-if="!isActive"
                is-link
                size="M"
                font-icon="fn_angleLink"
                type="text"
                :content="config.ctas.check"
            />
        </div>
    </router-link>
</template>

<script>
import Cta from 'gvue/src/core/Cta'
import Heading from 'gvue/src/core/Heading'
import Paragraph from 'gvue/src/core/Paragraph'
import Visual from 'gvue/src/core/Visual'

import getImage from '@/helpers/image'
import cleanUrl from '@/helpers/url'
import objectFit from '@/mixins/fit'

export default {
    name: 'SliderEditionsCard',
    mixins: [objectFit],
    components: {
        Cta,
        Heading,
        Paragraph,
        Visual
    },
    props: {
        edition: {
            type: Object,
            required: true
        },
        current: {
            type: String,
            required: true
        }
    },
    computed: {
        isActive() {
            return this.edition.number === this.current
        },
        surtitle() {
            return `${this.config.edition} ${this.edition.number}`
        },
        cover() {
            return getImage({
                type: 'visual',
                name: `edition_${this.edition.number}_thumb`,
                ext: 'jpg'
            })
        },
        classes() {
            return {
                active: this.isActive
            }
        },
        styles() {
            return {
                caption: { borderColor: this.edition.color }
            }
        },
        config() {
            return this.$json.config
        },
        language() {
            const { language } = this.$landing
            return language
        },
        title() {
            return cleanUrl(this.edition.title)
        }
    }
}
</script>

<style lang="sass" scoped>
@import ~nespresso-bi/utilities
@import ~nespresso-bi/brand/variables
.card
    position: relative
    display: flex
    transition: opacity $easing $duration_medium
    color: inherit

    &::before
        +fit
        content: ''
        opacity: 0
        box-shadow: 0 1px 2px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.07), 0 4px 8px rgba(0,0,0,0.07)
        transition: opacity $easing $duration_medium

    &.active
        pointer-events: none

        &::before
            opacity: 1

    + .card
        margin-top: $gap

.cover
    position: relative
    z-index: 0
    flex: 0 0 27vw
    max-width: 27vw

    .g_visual
        position: absolute
        top: 0
        left: 0
        height: 100%
        width: 100%
        object-fit: cover
        object-position: center

.caption
    display: flex
    flex-direction: column
    flex: 0 0 73vw
    max-width: 73vw
    padding: ($gap * 1.5) ($gap * 1.25) ($gap * 1.75)
    background-color: $color_background
    border-left: 4px solid

.g
    .g_h2
        margin: ($gap * 0.25) 0 ($gap * 0.875)
        font-size: 1.5em
        letter-spacing: 0.15em
        line-height: 1.2
        text-transform: none

    .g_h5
        margin: 0
        font-weight: 600
        letter-spacing: 0.1em
        text-transform: uppercase

    .g_link
        margin: auto 0 0
        cursor: pointer

        &:hover ::v-deep span
            text-decoration: none !important

    [class*="g_txt"]
        margin-bottom: ($gap * 0.75)

+desktop
    .card
        height: 100%
        cursor: pointer

        &::before
            box-shadow: 0 1px 2px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.07), 0 4px 8px rgba(0,0,0,0.07), 0 8px 16px rgba(0,0,0,0.07), 0 16px 32px rgba(0,0,0,0.07), 0 32px 64px rgba(0,0,0,0.07)

        &:hover
            &::before
                opacity: 1
    .cover
        flex: 0 0 47%
        max-width: 47%

    .caption
        flex: 0 0 53%
        max-width: 53%
        padding: ($gap * 2) ($gap * 1.25)
        background-color: darken($color_background, 3%)

    .g
        .g_h2
            margin-top: ($gap * 0.5)
            margin-bottom: ($gap * 0.625)

    .caption
        border-width: 8px
</style>
